import React from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useReorderBoardNodes, useUpdateBoardNode } from 'hooks/Board';
import { BoardNodes } from 'types/BoardNode';
import { transformAllSparkBoardNodesToRectFlowNodes } from 'components/common/SparkOutputBoard/helper/useReactFlowHelpers';
import type { Node } from 'reactflow';

type UseBoardNodeCRUDProps = {
  boardID: string | null | undefined;
  boardNodes?: BoardNodes[];
  setAllBoardNodes?: (nodes: Node[]) => void | undefined;
};

export function useBoardNodeCRUD({
  boardID,
  boardNodes,
  setAllBoardNodes,
}: UseBoardNodeCRUDProps) {
  const { mutateAsync: reorderBoardNodes, isLoading: isReorderingBoardNodes } =
    useReorderBoardNodes(boardID);
  const { mutateAsync: updateBoardNode, isLoading: isUpdatingBoardNode } =
    useUpdateBoardNode(null, boardID || '');

  // Memoize sorted items to avoid recreation on each render
  const sortableItems = React.useMemo(
    () => boardNodes?.map((option) => option) || [],
    [boardNodes]
  );

  // Handle drag-and-drop functionality
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || !boardNodes?.length) return;

      if (active.id !== over.id) {
        const draggedIndex = boardNodes.findIndex(
          (item) => item.id === active.id
        );
        const overIndex = boardNodes.findIndex((item) => item.id === over.id);
        const updated = arrayMove(boardNodes, draggedIndex, overIndex);
        reorderBoardNodes(updated);
        setAllBoardNodes &&
          setAllBoardNodes(transformAllSparkBoardNodesToRectFlowNodes(updated));
      }
    },
    [boardNodes, reorderBoardNodes, setAllBoardNodes]
  );

  const handleUpdateNode = React.useCallback(
    (node: BoardNodes['attributes'] & { id: string }) => {
      updateBoardNode(node);
    },
    [updateBoardNode]
  );

  const handleDeleteNode = React.useCallback(
    (nodeID: string) => {
      updateBoardNode({ id: nodeID, isDeleted: true });
    },
    [updateBoardNode]
  );

  return {
    handleDeleteNode,
    handleUpdateNode,
    handleDragEnd,
    sortableItems,
    isReorderingBoardNodes,
    isUpdatingBoardNode,
  };
}
